/**
 * RVA Yeast Labs — Richmond, Virginia.
 *
 * A regional liquid-yeast lab whose catalogue is organised the way a brewer
 * shops: numbered by family, and named after the brewery each strain came from
 * where that is the honest description of it. Their two wine strains and the
 * champagne yeast are deliberately absent — this is a beer catalog and the
 * schema's yeast types have nowhere to put them.
 *
 * Temperatures are converted from the °F bands RVA publish. The four
 * Brettanomyces carry no published analysis at all, which is ordinary for Brett
 * and is why they are `representative` and say so.
 */

import type { Yeast } from '../../schema/yeast';

export const rvaYeasts: readonly Yeast[] = [
  {
    id: 'rva-101',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 101',
    name: 'Chico Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 81 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Chico strain from a Virginia lab: clean, crisp and deliberately quiet, so the hops and malt carry the beer. The default for an American pale or IPA and forgiving enough to be a house strain.',
    tags: ['chico', 'clean', 'neutral', 'american', 'pale-ale', 'ipa'],
    url: 'https://rvayeastlabs.com/product/rva-101-chico-ale-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-103',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 103',
    name: 'Pacman Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Rogue strain: dry and clean with very little diacetyl and only a whisper of ester. It drops harder than Chico, which makes it the easier of the two to package quickly.',
    tags: ['pacman', 'rogue', 'clean', 'dry', 'american', 'versatile'],
    url: 'https://rvayeastlabs.com/product/rva-103-pacman-ale-rogue/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-104',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 104',
    name: 'Hoptopper Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 78 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Isolated from a celebrated New England IPA, and it behaves like it: soft, fruity and slow to drop, which is the haze doing its job. Ferment it warm and let the hops biotransform.',
    tags: ['neipa', 'hazy', 'alchemist', 'conan', 'juicy', 'low-flocculation'],
    url: 'https://rvayeastlabs.com/product/rva-104-hoptopper-ale-alchemist/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-131',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 131',
    name: 'Chiswick Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 72 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Fuller’s strain, and the most emphatically English thing in the range: marmalade esters, residual sweetness and flocculation so hard the beer clears itself. Attenuation stops at 72 percent on purpose.',
    tags: ['english', 'fullers', 'bitter', 'esb', 'marmalade', 'high-flocculation'],
    url: 'https://rvayeastlabs.com/product/rva-131-chiswick-ale-fullers/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-132',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 132',
    name: 'Manchester Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Drier than the Chiswick and a shade more expressive: a touch of ester over a clean English base. For bitters and milds that should finish rather than linger.',
    tags: ['english', 'boddingtons', 'bitter', 'mild', 'dry', 'esters'],
    url: 'https://rvayeastlabs.com/product/rva-132-manchester-ale-boddingtons/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-141',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 141',
    name: 'Scotch Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The McEwan’s strain: malt-forward, restrained, and the reason a wee heavy tastes of caramel and not of yeast. Keep it cool and it leaves the body a winter beer needs.',
    tags: ['scottish', 'wee-heavy', 'malty', 'winter-warmer', 'mcewans'],
    url: 'https://rvayeastlabs.com/product/rva-141-scotch-ale-mcewans/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-151',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 151',
    name: 'Dublin Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Irish stout strain: crisp, dry, with the faint diacetyl that a dry stout is supposed to have rather than the fault it would be anywhere else.',
    tags: ['irish', 'stout', 'dry-stout', 'diacetyl', 'crisp'],
    url: 'https://rvayeastlabs.com/product/rva-151-dublin-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-161',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 161',
    name: 'German Ale I',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 16, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A kölsch-style strain that throws DMS early and cleans it up itself, so do not panic at day two. Ferment cold for a delicate, lager-like ale.',
    tags: ['kolsch', 'german', 'hybrid', 'clean', 'delicate', 'altbier'],
    url: 'https://rvayeastlabs.com/product/rva-161-german-ale-i/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-201',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 201',
    name: 'Trappist Ale I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Chimay strain, and the fruit is the point: plum and dark stone fruit over a dry Belgian finish. The starting place for a dubbel.',
    tags: ['belgian', 'trappist', 'chimay', 'dubbel', 'plum', 'esters'],
    url: 'https://rvayeastlabs.com/product/rva-201-trappist-ale-i-chimay/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-202',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 202',
    name: 'Trappist Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Orval house strain: quieter in ester than the Chimay and built for high gravity. Pair it with Brettanomyces if you want the whole Orval story.',
    tags: ['belgian', 'trappist', 'orval', 'high-gravity', 'restrained'],
    url: 'https://rvayeastlabs.com/product/rva-202-trappist-ale-ii-orval/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-203',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 203',
    name: 'Trappist Ale III',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The cleanest of the four Trappists, which makes it the one to choose when the malt should lead: dubbel and tripel where the grist is doing the talking.',
    tags: ['belgian', 'trappist', 'westmalle', 'tripel', 'dubbel', 'clean'],
    url: 'https://rvayeastlabs.com/product/rva-203-trappist-ale-iii-westmalle/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-204',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 204',
    name: 'Trappist Ale IV',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Rochefort strain: fruit-forward across single, dubbel and tripel, and the most characterful of the range. Give it headspace and a warm finish.',
    tags: ['belgian', 'trappist', 'rochefort', 'quadrupel', 'fruity', 'dark-strong'],
    url: 'https://rvayeastlabs.com/product/rva-204-trappist-ale-iv-rochefort/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-221',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 221',
    name: 'Belgian 1833',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The De Koninck strain, and the narrow 19-21 °C band is not a suggestion: held there it gives the soft, faintly spicy Belgian pale that the brewery is known for.',
    tags: ['belgian', 'de-koninck', 'pale-ale', 'spicy', 'narrow-range'],
    url: 'https://rvayeastlabs.com/product/rva-221-belgian-1833-de-konick/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-222',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 222',
    name: 'Belgian Gnome',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 19, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The La Chouffe strain: pepper and orchard fruit, high flocculation for a Belgian, and a wide enough range to push the esters where you want them.',
    tags: ['belgian', 'la-chouffe', 'golden-strong', 'peppery', 'fruity'],
    url: 'https://rvayeastlabs.com/product/rva-222-belgian-gnome-la-chouffe/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-241',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 241',
    name: 'Verboten Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Hoegaarden witbier strain, flocculating low so the beer stays white. Coriander and orange peel are the brewer’s job; the spice and the haze are this yeast’s.',
    tags: ['belgian', 'witbier', 'hoegaarden', 'hazy', 'spicy', 'low-flocculation'],
    url: 'https://rvayeastlabs.com/product/rva-241-verboten-ale-hoegaarden/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-251',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 251',
    name: 'Golden Strong Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Delirium strain from Huyghe: pear, pepper and enough attenuation to leave a nine percent beer tasting light. The Belgian golden strong in a vial.',
    tags: ['belgian', 'golden-strong', 'huyghe', 'delirium', 'pear', 'peppery'],
    url: 'https://rvayeastlabs.com/product/rva-251-golden-strong-ale-huyghe/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-261',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 261',
    name: 'Saison I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 75 },
    temperatureC: { min: 24, max: 29 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Dupont strain, and it wants the heat: 24 to 29 °C, which is where it stops stalling and starts tasting like saison. Attenuation reads low here because it finishes slowly rather than shyly.',
    tags: ['saison', 'dupont', 'farmhouse', 'hot-ferment', 'peppery', 'belgian'],
    url: 'https://rvayeastlabs.com/product/rva-261-saison-i-dupont/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-262',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 262',
    name: 'Saison II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 19, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Thiriez strain: a saison for brewers who do not want to ferment at 29 °C. Attenuates further than the Dupont and finishes without the stall.',
    tags: ['saison', 'thiriez', 'farmhouse', 'french', 'reliable', 'dry'],
    url: 'https://rvayeastlabs.com/product/rva-262-saison-ii-brasserie-thiriez/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-263',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 263',
    name: 'Ghost Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 77, max: 84 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Fantôme strain, and the widest temperature range in the range at 18 to 27 °C: the warmer it runs the stranger and more expressive it gets. Attenuates hard.',
    tags: ['saison', 'fantome', 'farmhouse', 'expressive', 'wide-range', 'dry'],
    url: 'https://rvayeastlabs.com/product/rva-263-ghost-ale-fantme/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-301',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 301',
    name: 'American Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A clean American lager strain in a tight cold band, attenuating to 80 percent for the crisp, dry finish a pre-Prohibition or light lager wants.',
    tags: ['lager', 'american', 'clean', 'crisp', 'cold', 'light-lager'],
    url: 'https://rvayeastlabs.com/product/rva-301-american-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-302',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 302',
    name: 'München Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 10, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Munich lager strain with unusual latitude — 10 to 18 °C — and attenuation held to 75 percent, which is what keeps a dunkel or helles malty rather than thin.',
    tags: ['lager', 'munich', 'malty', 'helles', 'dunkel', 'wide-range'],
    url: 'https://rvayeastlabs.com/product/rva-302-munchen-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-303',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 303',
    name: 'Oktoberfest Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 65, max: 73 },
    temperatureC: { min: 11, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The märzen strain: the lowest attenuation of the lagers at 65 to 73 percent, which is the malt sweetness a festbier is built on.',
    tags: ['lager', 'marzen', 'oktoberfest', 'festbier', 'malty'],
    url: 'https://rvayeastlabs.com/product/rva-303-oktoberfest-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-304',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 304',
    name: 'Czech Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Bohemian pilsner strain: cold, well attenuated and faintly sulfurous while it works. Give it a diacetyl rest and a long cold conditioning.',
    tags: ['lager', 'czech', 'pilsner', 'bohemian', 'sulfur', 'crisp'],
    url: 'https://rvayeastlabs.com/product/rva-304-czech-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-310',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 310',
    name: 'Fritz Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 14, max: 18 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A lager strain that works at ale temperatures — 14 to 18 °C — and flocculates high, which together make it the practical choice for a brewery without a cold room.',
    tags: ['lager', 'warm-lager', 'high-flocculation', 'practical', 'clean'],
    url: 'https://rvayeastlabs.com/product/rva-310-fritz-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-401',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 401',
    name: 'Hefeweizen I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The banana end of the weissbier spectrum: low flocculation, cloudy by design, and more fruit than clove at the warm end of its band.',
    tags: ['weissbier', 'hefeweizen', 'banana', 'german', 'hazy', 'low-flocculation'],
    url: 'https://rvayeastlabs.com/product/rva-401-hefeweizen-i/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-402',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 402',
    name: 'Hefeweizen II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The clove end: a tighter, cooler band than Hefeweizen I and a more phenolic result. The one for a weissbier that should taste of spice rather than of fruit.',
    tags: ['weissbier', 'hefeweizen', 'clove', 'phenolic', 'german', 'hazy'],
    url: 'https://rvayeastlabs.com/product/rva-402-hefeweizen-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-403',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 403',
    name: 'Hefeweizen III',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The most attenuative of the three weissbier strains, finishing drier while keeping the haze. For a lighter-bodied wheat beer that still reads as a hefeweizen.',
    tags: ['weissbier', 'hefeweizen', 'dry', 'german', 'hazy', 'attenuative'],
    url: 'https://rvayeastlabs.com/product/rva-403-hefeweizen-iii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-501',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 501',
    name: 'Brettanomyces claussenii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The gentlest of the Bretts: pineapple and light fruit rather than barnyard, and slow. RVA publish no analysis for their Brettanomyces, so the numbers here are typical of the species rather than theirs.',
    tags: ['brettanomyces', 'claussenii', 'fruity', 'pineapple', 'secondary', 'mild'],
    url: 'https://rvayeastlabs.com/product/rva-501-brettanomyces-claussenii/',
    provenance: 'representative',
  },
  {
    id: 'rva-502',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 502',
    name: 'Brettanomyces bruxellensis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The classic barnyard Brett: leather, hay and a slow dry finish over months rather than weeks. Figures are typical for the species; RVA publish none.',
    tags: ['brettanomyces', 'bruxellensis', 'funk', 'barnyard', 'lambic', 'secondary'],
    url: 'https://rvayeastlabs.com/product/rva-502-brettanomyces-bruxellensis/',
    provenance: 'representative',
  },
  {
    id: 'rva-503',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 503',
    name: 'Brettanomyces lambicus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The cherry-and-horse-blanket Brett, the one that tastes most like a traditional lambic. Species-typical figures; RVA publish no analysis.',
    tags: ['brettanomyces', 'lambicus', 'cherry', 'funk', 'lambic', 'secondary'],
    url: 'https://rvayeastlabs.com/product/rva-503-brettanomyces-lambicus/',
    provenance: 'representative',
  },
  {
    id: 'rva-504',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 504',
    name: 'Brettanomyces Beersel',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Brett isolate named for the Belgian town at the heart of lambic country. Species-typical figures; RVA publish no analysis for it.',
    tags: ['brettanomyces', 'beersel', 'belgian', 'funk', 'lambic', 'secondary'],
    url: 'https://rvayeastlabs.com/product/rva-504-brettanomyces-beersel/',
    provenance: 'representative',
  },
  {
    id: 'rva-805',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 805',
    name: 'Piedmont Hops Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A native yeast captured in the Virginia piedmont: a clean, moderate ale strain with a local provenance rather than a brewery pedigree.',
    tags: ['native', 'virginia', 'wild-caught', 'local', 'clean', 'ale'],
    url: 'https://rvayeastlabs.com/product/rva-805-piedmont-hops-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-806',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 806',
    name: 'Lickinghole Creek Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The most attenuative strain RVA list, finishing as far as 85 percent. Captured at a Virginia farm brewery, and dry enough to suit a saison as much as an ale.',
    tags: ['native', 'virginia', 'wild-caught', 'dry', 'attenuative', 'farmhouse'],
    url: 'https://rvayeastlabs.com/product/rva-806-lickinghole-creek-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rva-807',
    lab: 'RVA Yeast Labs',
    productCode: 'RVA 807',
    name: 'Dunnottar Castle Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A native isolate that attenuates like a clean ale strain and drops well. RVA publish attenuation and flocculation for it but no temperature band, so the range here is the ordinary ale one.',
    tags: ['native', 'wild-caught', 'clean', 'ale', 'medium-high-flocculation'],
    url: 'https://rvayeastlabs.com/product/rva-807-dunnottar-castle-ale/',
    provenance: 'representative',
  },
];
